import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import usePerformanceData from '../../hooks/usePerformanceData';
import { 
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  UsersIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { 
  ChartBarIcon as ChartBarSolid,
  CalendarIcon as CalendarSolid,
  UserGroupIcon as UserGroupSolid,
  CurrencyDollarIcon as CurrencyDollarSolid,
  BriefcaseIcon as BriefcaseSolid,
  BookmarkIcon
} from '@heroicons/react/24/solid';
import { 
  formatRevenue, 
  formatGrowth, 
  formatPtBrNumber, 
  getTrendColor 
} from '../../utils';
import './sections/style.css';

// Componentes que serão criados a seguir
import OverviewSection from './sections/OverviewSection';
import AppointmentsSection from './sections/AppointmentsSection';
import FinancialSection from './sections/FinancialSection';
import TeamSection from './sections/TeamSection';
import ClientsSection from './sections/ClientsSection';
import ServicesSection from './sections/ServicesSection';

// Constante para seções de página
const SECTIONS = {
  OVERVIEW: 'visão geral',
  APPOINTMENTS: 'agendamentos',
  FINANCIAL: 'financeiro',
  TEAM: 'equipe',
  CLIENTS: 'clientes',
  SERVICES: 'serviços'
};

const PerformancePage = ({ salonId }) => {
  const [activeSection, setActiveSection] = useState(SECTIONS.OVERVIEW);
  const [dateRange, setDateRange] = useState('thisMonth');
  const [isMobile, setIsMobile] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [safeAreaTop, setSafeAreaTop] = useState(0);

  const { 
    metrics, 
    appointments, 
    loading, 
    error: dataError 
  } = usePerformanceData(salonId, dateRange);

  // Verificar se o dispositivo é mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Detectar o safe area inset para iOS - Similar ao AdminHeader
  useEffect(() => {
    // Se não estiver em mobile, não precisamos executar este efeito
    if (!isMobile) return;
    
    // Função para detectar e calcular o safe area top
    const detectSafeArea = () => {
      // Para iOS com notch, podemos usar env() se disponível
      if (CSS.supports('padding-top: env(safe-area-inset-top)')) {
        const computedStyle = getComputedStyle(document.documentElement);
        const safeTop = computedStyle.getPropertyValue('--sat') || '0px';
        const safeTopValue = parseInt(safeTop, 10);
        setSafeAreaTop(isNaN(safeTopValue) ? 0 : safeTopValue);
      } else {
        // Fallback - detectar tamanho aproximado baseado na altura da janela
        const isNotchLikelyPresent = 
          (window.screen.height >= 812 && window.devicePixelRatio >= 2) || // iPhone X ou superior
          (navigator.userAgent.includes('iPhone') && window.innerHeight > 800);
        
        setSafeAreaTop(isNotchLikelyPresent ? 44 : 20);
      }
    };
    
    // Adicionamos CSS variables para usar o env()
    document.documentElement.style.setProperty('--sat', 'env(safe-area-inset-top)');
    
    detectSafeArea();
    window.addEventListener('resize', detectSafeArea);
    
    return () => window.removeEventListener('resize', detectSafeArea);
  }, [isMobile]);

  // Função para atualizar dados
  const refreshData = useCallback(() => {
    setIsRefreshing(true);
    // Simular atualização de dados
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  }, []);

  // Renderizar a seção ativa
  const renderActiveSection = () => {
    switch (activeSection) {
      case SECTIONS.OVERVIEW:
        return <OverviewSection metrics={metrics} appointments={appointments} isMobile={isMobile} />;
      case SECTIONS.APPOINTMENTS:
        return <AppointmentsSection metrics={metrics} appointments={appointments} isMobile={isMobile} />;
      case SECTIONS.FINANCIAL:
        return <FinancialSection metrics={metrics} isMobile={isMobile} />;
      case SECTIONS.TEAM:
        return <TeamSection metrics={metrics} isMobile={isMobile} />;
      case SECTIONS.CLIENTS:
        return <ClientsSection metrics={metrics} isMobile={isMobile} />;
      case SECTIONS.SERVICES:
        return <ServicesSection metrics={metrics} isMobile={isMobile} />;
      default:
        return <OverviewSection metrics={metrics} appointments={appointments} isMobile={isMobile} />;
    }
  };

  if (loading || dataError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="w-20 h-20 relative"
        >
          <motion.div 
            className="absolute inset-0 rounded-full border-t-2 border-blue-500"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-4 text-blue-600 font-medium"
        >
          Preparando seus insights...
        </motion.p>
        {dataError && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-2 text-red-500 text-sm"
          >
            {dataError}
          </motion.p>
        )}
      </div>
    );
  }

  // Calcular estilos para o safe area
  const safeAreaStyle = isMobile ? { paddingTop: `max(env(safe-area-inset-top), ${safeAreaTop}px)` } : {};

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="flex flex-col w-full min-h-screen bg-gray-50"
    >
      {/* Cabeçalho da página */}
      <div className="bg-white shadow-sm" style={safeAreaStyle}>
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Desempenho
              </h1>
              <p className="text-sm text-gray-600">
                Acompanhe seus resultados e identifique oportunidades
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="today">Hoje</option>
                <option value="thisWeek">Esta semana</option>
                <option value="thisMonth">Este mês</option>
                <option value="last3Months">Últimos 3 meses</option>
                <option value="thisYear">Este ano</option>
              </select>
              <button 
                onClick={refreshData}
                disabled={isRefreshing}
                className={`p-2 rounded-full ${isRefreshing ? 'bg-gray-200' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
              >
                <ArrowPathIcon className={`w-5 h-5 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isRefreshing && (
        <div className="w-full h-1 bg-gray-100">
          <div className="h-1 animated-progress-bar"></div>
        </div>
      )}

      {/* Navegação de seções */}
      <div className="bg-white shadow-sm mb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navegação em desktop/tablet */}
          <div className="hidden md:block">
            <nav className="flex space-x-1 py-2">
              {Object.values(SECTIONS).map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`px-4 py-2 text-sm font-medium rounded-md capitalize whitespace-nowrap smooth-transition ${
                    activeSection === section
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {section === SECTIONS.OVERVIEW && <ChartBarIcon className="w-4 h-4 inline mr-1" />}
                  {section === SECTIONS.APPOINTMENTS && <CalendarIcon className="w-4 h-4 inline mr-1" />}
                  {section === SECTIONS.FINANCIAL && <CurrencyDollarIcon className="w-4 h-4 inline mr-1" />}
                  {section === SECTIONS.TEAM && <UserGroupIcon className="w-4 h-4 inline mr-1" />}
                  {section === SECTIONS.CLIENTS && <UsersIcon className="w-4 h-4 inline mr-1" />}
                  {section === SECTIONS.SERVICES && <ClipboardDocumentListIcon className="w-4 h-4 inline mr-1" />}
                  {section}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Navegação em mobile - apenas ícones com indicador embaixo */}
          <div className="md:hidden">
            <div className="relative mx-auto" style={{ width: "calc(85vw + 20px)", paddingRight: "20px" }}>
              <div className="overflow-x-auto hide-scrollbar pr-6">
                <nav className="flex space-x-3 py-3 min-w-max pb-4 pl-1">
                  {Object.values(SECTIONS).map((section) => (
                    <button
                      key={section}
                      onClick={() => setActiveSection(section)}
                      className={`flex flex-col items-center px-4 py-2 rounded-md smooth-transition flex-shrink-0 ${
                        activeSection === section
                          ? 'text-blue-600'
                          : 'text-gray-500'
                      }`}
                      style={{ minWidth: "80px" }}
                    >
                      <div className={`p-2.5 rounded-full ${activeSection === section ? 'bg-blue-50' : ''}`}>
                        {section === SECTIONS.OVERVIEW && <ChartBarIcon className="w-6 h-6" />}
                        {section === SECTIONS.APPOINTMENTS && <CalendarIcon className="w-6 h-6" />}
                        {section === SECTIONS.FINANCIAL && <CurrencyDollarIcon className="w-6 h-6" />}
                        {section === SECTIONS.TEAM && <UserGroupIcon className="w-6 h-6" />}
                        {section === SECTIONS.CLIENTS && <UsersIcon className="w-6 h-6" />}
                        {section === SECTIONS.SERVICES && <ClipboardDocumentListIcon className="w-6 h-6" />}
                      </div>
                      <span className="text-xs capitalize mt-1 font-medium whitespace-nowrap">{section}</span>
                      {activeSection === section && (
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1"></div>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
              
              {/* Indicadores de scroll - apenas à direita */}
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none nav-scroll-indicator"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            {renderActiveSection()}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default PerformancePage;
